import { formatDate, isDateAfter, parseDate, pluralize } from '@travelata-libs/utils';
import Cookies from 'js-cookie';
import { buildHistoryCriteriaParams } from './searchCriteria';
import type { SearchCriteria, SearchCriteriaHistory, Selection } from '../model/types';
import getWildcardDomain from '@/shared/lib/getWildcardDomain';
import type { SearchCriteriaParams } from '@/shared/model';

const HISTORY_STORAGE_KEY = 'tr_historySearch';
const LAST_SEARCH_COOKIE_KEY = 'last_search_criteria';
const LAST_SEARCH_COOKIE_EXPIRES_DAYS = 1;
const MAX_HISTORY_ITEMS = 5;


const HISTORY_DATE_FORMAT = 'dd.MM.yyyy';

const RESORT_FORMS = ['курорт', 'курорта', 'курортов'];


function toHistoryDate(value: string): string {
  const parsed = parseDate(value);
  return parsed ? formatDate(parsed, HISTORY_DATE_FORMAT) : value;
}

function getDescription(destination: Selection | null): string {
  if (!destination || destination.kind === 'none') {
    return '';
  }

  if (destination.kind === 'hotels') {
    return destination.hotels.map((hotel) => hotel.name).join(', ');
  }

  if (destination.kind === 'resorts') {
    const { resorts, country } = destination;

    if (resorts.length === 1) {
      return resorts[0].name;
    }
    if (resorts.length > 1) {
      return `${country.name}, ${pluralize(RESORT_FORMS, resorts.length, true)}`;
    }

    return country.name;
  }

  return destination.country.name;
}


function toHistoryParams(criteria: SearchCriteria): SearchCriteriaParams {
  const params = buildHistoryCriteriaParams(criteria);

  return {
    ...params,
    checkInDateRange: {
      ...params.checkInDateRange,
      from: toHistoryDate(params.checkInDateRange.from),
      to: toHistoryDate(params.checkInDateRange.to),
    },
  };
}

function readHistory(): SearchCriteriaHistory[] {
  if (typeof localStorage === 'undefined') {
    return [];
  }

  try {
    const parsed = JSON.parse(localStorage.getItem(HISTORY_STORAGE_KEY) || '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}


function isFutureSearch(record: SearchCriteriaHistory): boolean {
  const from = record.checkInDateRange?.from;
  const checkIn = from ? parseDate(from) : null;
  return checkIn ? isDateAfter(checkIn, new Date()) : false;
}

function stableStringify(value: unknown): string {
  if (Array.isArray(value)) {
    return `[${value.map((item) => stableStringify(item)).join(',')}]`;
  }
  if (value && typeof value === 'object') {
    const entries = Object.keys(value as Record<string, unknown>)
      .sort()
      .map((key) => `${JSON.stringify(key)}:${stableStringify((value as Record<string, unknown>)[key])}`);
    return `{${entries.join(',')}}`;
  }
  return JSON.stringify(value ?? null);
}

function criteriaKey(source: SearchCriteriaParams): string {
  return stableStringify({
    checkInDateRange: source.checkInDateRange,
    countries: source.countries,
    departureCity: source.departureCity,
    nightRange: source.nightRange,
    resorts: source.resorts,
    hotels: source.hotels,
    touristGroup: source.touristGroup,
  });
}

function saveToHistory(record: SearchCriteriaHistory, params: SearchCriteriaParams): void {
  if (typeof localStorage === 'undefined') {
    return;
  }

  const actualHistory = readHistory().filter((item) => isFutureSearch(item));
  const key = criteriaKey(params);
  if (actualHistory.some((item) => criteriaKey(item) === key)) {
    return;
  }

  const nextHistory = [record, ...actualHistory].slice(0, MAX_HISTORY_ITEMS);

  try {
    localStorage.setItem(HISTORY_STORAGE_KEY, JSON.stringify(nextHistory));
  } catch {
    // Квота / приватный режим — история не критична, не роняем поиск.
  }
}

function saveLastSearchCookie(params: SearchCriteriaParams): void {
  if (typeof document === 'undefined') {
    return;
  }

  Cookies.set(LAST_SEARCH_COOKIE_KEY, JSON.stringify(params), {
    domain: getWildcardDomain(),
    path: '/',
    expires: LAST_SEARCH_COOKIE_EXPIRES_DAYS,
  });
}

export default function addSearch(criteria: SearchCriteria): void {
  const params = toHistoryParams(criteria);
  const record: SearchCriteriaHistory = {
    ...params,
    description: getDescription(criteria.destination),
  };

  saveToHistory(record, params);
  saveLastSearchCookie(params);
}
